import React, { useRef, useState } from "react";
import { Volume2, Loader2, Square } from "lucide-react";
import { speakText, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const base64ToBlob = (b64, type) => {
  const bin = atob(b64);
  const len = bin.length;
  const buf = new Uint8Array(len);
  for (let i = 0; i < len; i++) buf[i] = bin.charCodeAt(i);
  return new Blob([buf], { type });
};

/** Tap-to-listen button that calls /api/voice/speak and plays the returned MP3.
 *  Tapping again while playing stops playback. Audio URLs are revoked on cleanup.
 */
export const SpeakButton = ({ text, companionId, voice, engine, className = "", size = "sm" }) => {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const cleanup = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  const toggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (state === "playing") {
      cleanup();
      setState("idle");
      return;
    }
    if (!text || !text.trim()) return;
    setState("loading");
    try {
      const { audio_base64, content_type } = await speakText({
        text,
        companion_id: companionId,
        voice,
        engine,
      });
      const blob = base64ToBlob(audio_base64, content_type || "audio/mpeg");
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        cleanup();
        setState("idle");
      };
      audio.onerror = () => {
        cleanup();
        setState("idle");
        toast.error("Playback failed");
      };
      await audio.play();
      setState("playing");
    } catch (err) {
      setState("idle");
      cleanup();
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      toast.error(msg);
    }
  };

  React.useEffect(() => cleanup, []); // cleanup on unmount

  const dim = size === "xs" ? "h-6 w-6" : size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const icon = size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5";

  return (
    <button
      type="button"
      data-testid="speak-btn"
      aria-label={state === "playing" ? "Stop playback" : "Read aloud"}
      onClick={toggle}
      disabled={!text || state === "loading"}
      className={`inline-flex items-center justify-center rounded-full border transition-colors ${dim} ${
        state === "playing"
          ? "bg-amber-500/20 border-amber-400/60 text-amber-200"
          : state === "loading"
          ? "bg-white/5 border-white/10 text-slate-500"
          : "bg-white/5 border-white/10 text-slate-400 hover:border-amber-400/60 hover:text-amber-300"
      } ${className}`}
      title={state === "playing" ? "Stop" : "Read aloud"}
    >
      {state === "loading" ? (
        <Loader2 className={`${icon} animate-spin`} />
      ) : state === "playing" ? (
        <Square className={icon} />
      ) : (
        <Volume2 className={icon} />
      )}
    </button>
  );
};

export default SpeakButton;
